/**
 * Member service - Trip collaboration: invite links, preview, join, members
 * Integrates with backend /trip API (see specs/006-/contracts/member-api.md)
 */

import { apiGet, apiPost, apiPatch, apiDelete } from './apiClient';
import type {
  ChangeMemberRoleResponse,
  InviteLink,
  InviteLinkResponse,
  InvitePreview,
  InvitePreviewResponse,
  JoinTripResponse,
  MemberListResponse,
  MemberRole,
  RemoveMemberResponse,
  TripMember,
} from '../types/member';

/**
 * API error that preserves the backend exitCode, so callers can branch on
 * specific error conditions (e.g. INVITE_LINK_EXPIRED vs INVITE_LINK_NOT_FOUND)
 * instead of only showing a generic failure message.
 * See specs/006-/contracts/member-api.md for the exitCode table.
 */
export class MemberApiError extends Error {
  constructor(public code: number, message: string) {
    super(message);
    this.name = 'MemberApiError';
  }
}

/**
 * Generate (or regenerate) an invite link for a trip.
 * Regenerating invalidates the previous link. Owner only.
 */
export async function generateInviteLink(tripId: string): Promise<InviteLink> {
  const response = await apiPost<InviteLinkResponse>(`/trip/${tripId}/invite-link`);

  if (response.exitCode !== 1 || !response.data) {
    throw new MemberApiError(response.exitCode, response.message || '產生邀請連結失敗');
  }

  return response.data;
}

/**
 * Get the current active invite link for a trip. Owner only.
 * Throws MemberApiError(60002) if no link has been generated yet.
 */
export async function getInviteLink(tripId: string): Promise<InviteLink> {
  const response = await apiGet<InviteLinkResponse>(`/trip/${tripId}/invite-link`);

  if (response.exitCode !== 1 || !response.data) {
    throw new MemberApiError(response.exitCode, response.message || '尚未產生邀請連結');
  }

  return response.data;
}

/**
 * Preview a trip via an invite token, without joining. Any authenticated
 * user may call this (doesn't require existing membership).
 */
export async function previewInviteLink(token: string): Promise<InvitePreview> {
  const response = await apiGet<InvitePreviewResponse>(`/trip/invite-link/${token}/preview`);

  if (response.exitCode !== 1 || !response.data) {
    throw new MemberApiError(response.exitCode, response.message || '無法預覽此邀請連結');
  }

  return response.data;
}

/**
 * Join a trip using an invite token. Idempotent: calling again while already
 * a member still succeeds and returns the same trip info.
 */
export async function joinTrip(token: string): Promise<{ tripId: string | number; tripName: string }> {
  const response = await apiPost<JoinTripResponse>('/trip/join', { token });

  if (response.exitCode !== 1 || !response.data) {
    throw new MemberApiError(response.exitCode, response.message || '加入旅程失敗');
  }

  return response.data;
}

/**
 * Get the member list for a trip. Any member may call this.
 */
export async function getMembers(tripId: string): Promise<TripMember[]> {
  const response = await apiGet<MemberListResponse>(`/trip/${tripId}/members`);

  if (response.exitCode !== 1) {
    throw new MemberApiError(response.exitCode, response.message || '取得成員清單失敗');
  }

  return response.data;
}

/**
 * Change a member's role. Owner only. Currently only 'EDITOR' is a valid target.
 */
export async function changeMemberRole(
  tripId: string,
  targetUserId: number,
  role: MemberRole
): Promise<{ userId: number; role: MemberRole }> {
  const response = await apiPatch<ChangeMemberRoleResponse>(`/trip/${tripId}/members/${targetUserId}`, { role });

  if (response.exitCode !== 1 || !response.data) {
    throw new MemberApiError(response.exitCode, response.message || '變更角色失敗');
  }

  return response.data;
}

/**
 * Remove a member from the trip. Owner only.
 */
export async function removeMember(tripId: string, targetUserId: number): Promise<{ userId: number }> {
  const response = await apiDelete<RemoveMemberResponse>(`/trip/${tripId}/members/${targetUserId}`);

  if (response.exitCode !== 1 || !response.data) {
    throw new MemberApiError(response.exitCode, response.message || '移除成員失敗');
  }

  return response.data;
}

/**
 * Leave a trip. Non-owner members only (owner must transfer or delete the trip instead).
 */
export async function leaveTrip(tripId: string): Promise<void> {
  const response = await apiDelete<{ exitCode: number; message: string }>(`/trip/${tripId}/members/me`);

  if (response.exitCode !== 1) {
    throw new MemberApiError(response.exitCode, response.message || '退出旅程失敗');
  }
}

export const memberService = {
  generateInviteLink,
  getInviteLink,
  previewInviteLink,
  joinTrip,
  getMembers,
  changeMemberRole,
  removeMember,
  leaveTrip,
};
